import { PagedResultMapper } from '@csp/csp-common-mapper';
import {
  ApiOptions,
  EcodeStr,
  Maybe,
  Patient,
  PatientStatusType,
  PatientStudyStatusType,
  Site,
  Study,
  UserId,
} from '@csp/csp-common-model';
import { PatientFactory } from '@csp/csp-common-patient-mapper';
import { createFieldCriterionEq, createFieldCriterionIn } from '@csp/csp-common-user-query';
import { isDefined, StringUtil } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import { UserRestServiceV1 } from '@csp/dmdp-api-client';
import {
  CriteriaOperatorType,
  CriteriaType,
  largePage,
  Page,
  pageWithOneItem,
  QueryV1,
  RoleType,
} from '@csp/dmdp-api-common-dto';
import { StudyConfig } from '@csp/csp-common-config';
import { UserCriteriaFieldType } from '@csp/dmdp-api-user-dto';
import { PagedPatientQueryIn } from '../model/PagedPatientQueryIn';
import { PatientPage } from '../model/PatientPage';
import { PatientQueryMapper } from '../model/PatientQueryMapper';
import { PatientQueryIn } from '../model/PatientQueryIn';

const PATIENTS_WITH_SCREENED_FAILED: QueryV1 = {
  criterion: {
    operator: CriteriaOperatorType.AND,
    criteria: [
      createFieldCriterionEq(UserCriteriaFieldType.ROLE, RoleType.PATIENT),
      createFieldCriterionEq(UserCriteriaFieldType.CUSTOM_STATUS_TYPE, PatientStatusType.STUDY),
      createFieldCriterionIn(UserCriteriaFieldType.CUSTOM_STATUS_VALUE, [PatientStudyStatusType.SCREEN_FAILED]),
    ],
  },
};

const findByEcode = async (ecode: EcodeStr, apiOptions?: ApiOptions): Promise<Maybe<Patient>> => {
  const resp = await UserRestServiceV1.query(
    toRestOptions(apiOptions),
    {
      criterion: {
        fieldCriterion: {
          field: UserCriteriaFieldType.STUDY_ECODE,
          type: CriteriaType.EQ,
          values: [ecode],
        },
      },
    },
    pageWithOneItem(),
  );

  return resp.users?.[0] ? PatientFactory.from({ userV1: resp.users[0] }) : undefined;
};

// TODO: The query API does not let us search for "username". Specifically the identity used for a provider on an idpAccount.
// DMDP will update this with either a specific endpoint or updating the query API. JIRA ticket on DMDP side is DPL-2845
// This method should be revisited after release 2.1.
const findScreenFailedUserByUsername = async (username: string, apiOptions?: ApiOptions): Promise<Maybe<Patient>> => {
  const result = await UserRestServiceV1.query(toRestOptions(apiOptions), PATIENTS_WITH_SCREENED_FAILED, largePage());

  const foundUser = result.users?.find(user =>
    user.idpAccounts?.find(idpAccount => StringUtil.equalsInsensitive(idpAccount.identity, username.trim())),
  );

  return foundUser ? PatientFactory.from({ userV1: foundUser }) : undefined;
};

const toNextPage = ({ pageSize, count = true, next }: { pageSize: number; count?: boolean; next?: string }): Page => ({
  next,
  limit: pageSize,
  count,
});

const query = async (
  patientQueryIn: PagedPatientQueryIn,
  study?: Study,
  next?: string,
  studyConfig?: StudyConfig,
  apiOptions?: ApiOptions,
): Promise<PatientPage> => {
  if (patientQueryIn.criteria.userIds?.length === 0) {
    return PagedResultMapper.toPagedResult({ count: 0 }, []);
  }

  const { queryV1 } = PatientQueryMapper.from(patientQueryIn);

  const usersV1 = await UserRestServiceV1.query(
    toRestOptions(apiOptions),
    queryV1,
    toNextPage({ pageSize: patientQueryIn.pageSize, count: patientQueryIn.count, next }),
  );

  const patients =
    usersV1.users?.map(userV1 => PatientFactory.from({ userV1, studyConfig, sites: study?.sites })) ?? [];
  return PagedResultMapper.toPagedResult(usersV1.paging, patients);
};

const QUERY_ALL_MAX_ITERATIONS = 1_000_000;
const queryAll = async (
  patientQueryIn: PatientQueryIn,
  study?: Study,
  maxNumberOfPages = QUERY_ALL_MAX_ITERATIONS,
  studyConfig?: StudyConfig,
  apiOptions?: ApiOptions,
  pageSize: number = 1000,
): Promise<Patient[]> => {
  const patients: Patient[] = [];
  let patientPage: Maybe<PatientPage>;
  let iteration = 0;

  do {
    patientPage = await query(
      { ...patientQueryIn, pageSize, count: true },
      study,
      patientPage?.next,
      studyConfig,
      toRestOptions(apiOptions),
    );
    patients.push(...patientPage.data);
    iteration = iteration + 1;
  } while (patients.length < (patientPage?.count ?? 0) && iteration < maxNumberOfPages);

  return patients;
};

type GetPatientCommonProps = {
  studyConfig?: Maybe<Pick<StudyConfig, 'patientStatusMappings'>>;
  sites?: Site[];
  apiOptions?: ApiOptions;
};

type GetPatientProps = GetPatientCommonProps & { userId: UserId };
type GetPatientsProps = GetPatientCommonProps & { userIds: UserId[] };

const getPatient = async ({ userId, studyConfig, sites = [], apiOptions }: GetPatientProps): Promise<Patient> => {
  const userV1 = await UserRestServiceV1.getUserById(toRestOptions(apiOptions), userId);
  return PatientFactory.from({ userV1, sites, studyConfig });
};

const tryGetPatient = async ({
  userId,
  studyConfig,
  sites = [],
  apiOptions,
}: GetPatientProps): Promise<Maybe<Patient>> => {
  const userV1 = await UserRestServiceV1.tryGetUserById(toRestOptions(apiOptions), userId);
  if (userV1 === undefined) {
    return undefined;
  }
  return PatientFactory.from({ userV1, sites, studyConfig });
};

const getPatients = async ({ userIds, studyConfig, sites = [], apiOptions }: GetPatientsProps): Promise<Patient[]> => {
  if (userIds.length <= 0) {
    return [];
  }
  const { users = [] } = await UserRestServiceV1.getUsersByIds(toRestOptions(apiOptions), userIds);
  const patients = users.map(userV1 => {
    const patient = PatientFactory.from({ userV1, studyConfig, sites });
    return patient;
  });

  return patients;
};

const refetchPatient = async (
  patient: Patient,
  studyConfig?: Maybe<Pick<StudyConfig, 'patientStatusMappings'>>,
  apiOptions?: ApiOptions,
): Promise<Patient> =>
  PatientQueryService.getPatient({
    userId: patient.userId,
    studyConfig,
    sites: [patient.site].filter(isDefined),
    apiOptions,
  });

export const PatientQueryService = {
  findByEcode,
  findScreenFailedUserByUsername,
  query,
  queryAll,
  getPatient,
  tryGetPatient,
  getPatients,
  refetchPatient,
};
